import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Optional label rendered inline right of the switch. */
  label?: string;
}

/**
 * Discord-style toggle switch — same interface as a checkbox (works with
 * `checked` / `onChange` / `disabled`) but rendered as a horizontal pill.
 *
 * Pure CSS, no Radix dependency. Uses `peer` + sibling selectors so the
 * hidden input remains form-participating (submits values, focus-ring lands
 * on the track).
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, className, disabled, ...props },
  ref
) {
  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 select-none',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        className="peer sr-only"
        {...props}
      />
      <span
        className={cn(
          // Track
          'relative inline-block h-6 w-11 shrink-0 rounded-full transition-colors',
          'bg-foreground/20 peer-checked:bg-primary',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background',
          // Knob
          'after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full',
          'after:bg-white after:shadow after:transition-transform',
          'peer-checked:after:translate-x-5'
        )}
      />
      {label && <span className="text-sm">{label}</span>}
    </label>
  );
});
